import { createPortal } from 'preact/compat';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams.js';
import FoilPreview from './FoilPreview';
import { RADIUS } from '../lib/medievalTheme';

// Floating card-art preview shown when hovering a `[Name](card:id)`
// mention. Portaled to .app-canvas so position:fixed stays scoped to
// the canvas (transform: translateZ(0) on .app-canvas makes it the
// containing block) and so the preview escapes any chat-bubble /
// logbook-row clipping context.
//
// `rect` is canvas-local (see rectToCanvasRect in cardMentions.js).
// Anchors to the right of the pill when there's room, flips left when
// near the canvas edge. Same placement logic deck notes uses, so the
// preview behavior feels consistent everywhere mentions appear.
export default function CardHoverPreview({ card, rect, foiling = null }) {
  if (!card || !rect) return null;
  // When a `foiling` is supplied (e.g. hovering a foil unit-hand spell),
  // pick the matching printing so the foil image + FoilPreview branch show;
  // otherwise fall back to the standard (first) printing.
  const printing = (foiling
    ? card.printings?.find((p) => p.foiling === foiling)
    : null) || card.printings?.[0];
  const imageUrl = printing?.image_url;
  if (!imageUrl) return null;

  const isFoil = isFoilFinish(printing?.foiling);
  const isSite = !!card.played_horizontally;
  const widthCqw = isSite ? 19 : 14;
  const heightCqw = isSite ? widthCqw * (63 / 88) : widthCqw * (88 / 63);

  const cqw = rect.canvasWidth / 100;
  const widthPx = widthCqw * cqw;
  const heightPx = heightCqw * cqw;
  const gap = 0.75 * cqw;
  const placeRight = rect.right + widthPx + gap + 8 < rect.canvasWidth;
  const left = placeRight
    ? rect.right + gap
    : Math.max(8, rect.left - widthPx - gap);
  const top = Math.max(
    8,
    Math.min(rect.canvasHeight - heightPx - 8, rect.top - heightPx / 2 + (rect.bottom - rect.top) / 2),
  );

  // Sites store art portrait (63:88) but are played landscape. Wrap
  // the img in a landscape frame and rotate 90° to fill it, mirroring
  // the deck-builder sidebar preview. Normal cards render directly.
  const node = isSite ? (
    <div
      style={{
        position: 'fixed',
        top,
        left,
        width: `${widthCqw}cqw`,
        height: `${heightCqw}cqw`,
        zIndex: 270,
        pointerEvents: 'none',
        borderRadius: RADIUS.cardLandscape,
        overflow: 'hidden',
        boxShadow: '0 24px 48px rgba(0,0,0,0.75), 0 8px 16px rgba(0,0,0,0.55)',
      }}
    >
      <img
        src={imageUrl}
        alt={card.name}
        draggable={false}
        style={{
          position: 'absolute',
          width: `${heightCqw}cqw`,
          height: `${widthCqw}cqw`,
          top: `calc((${heightCqw}cqw - ${widthCqw}cqw) / 2)`,
          left: `calc((${widthCqw}cqw - ${heightCqw}cqw) / 2)`,
          transform: 'rotate(90deg)',
          transformOrigin: 'center center',
          objectFit: 'cover',
        }}
      />
      {isFoil ? (
        <FoilPreview imageUrl={imageUrl} zones={foilZonesForCard(card)} params={DEFAULT_FOIL_PARAMS} rotate90 />
      ) : null}
    </div>
  ) : (
    <div
      style={{
        position: 'fixed',
        top,
        left,
        width: `${widthCqw}cqw`,
        aspectRatio: '63 / 88',
        borderRadius: RADIUS.card,
        overflow: 'hidden',
        boxShadow: '0 24px 48px rgba(0,0,0,0.75), 0 8px 16px rgba(0,0,0,0.55)',
        zIndex: 270,
        pointerEvents: 'none',
      }}
    >
      <img
        src={imageUrl}
        alt={card.name}
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      {isFoil ? (
        <FoilPreview imageUrl={imageUrl} zones={foilZonesForCard(card)} params={DEFAULT_FOIL_PARAMS} />
      ) : null}
    </div>
  );

  const canvas = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
  return canvas ? createPortal(node, canvas) : node;
}
